import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ButtonSpinnerSlot } from '../ui/ButtonSpinner';
import { ModalShell } from '../ui/shell';
import { Btn, Field, Input, Select, FormGrid, Muted } from '../ui/primitives';
import NoticeDialog from '../ui/NoticeDialog';
import { generatePrompt } from './generateprompt';
import PromptDialog from './promptdialog';
import QuestionsShowdialog from './showquestion';
import axios from 'axios';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

const FormRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const GridField = styled(Field)`
  margin-bottom: 0;
  min-width: 0;
`;

const TwoColGrid = styled(FormGrid)`
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const DomainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DomainSectionTitle = styled.h4`
  margin: 0;
  font-size: 13px;
  font-weight: 750;
  color: ${({ theme }) => theme.text};
`;

const DomainCard = styled.div`
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.rMd};
  background: ${({ theme }) => theme.surface2};
`;

const DomainHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  font-weight: 650;
  color: ${({ theme }) => theme.text};
`;

const DomainPct = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.accentStrong};
`;

const SubList = styled.ul`
  margin: 8px 0 0;
  padding-left: 16px;
  font-size: 11.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.textMuted};
`;

const PromptBtnRow = styled.div`
  margin-top: 10px;
`;

/**
 * Update/create assessment modal — portal ModalShell + primitives; API payloads unchanged.
 */
export default function FormDialog({ open, handleClose, data, testId }) {
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const [id, setId] = useState(data.id || '');
  const [courseName, setCourseName] = useState(data.courseName || '');
  const [examName, setExamName] = useState(data.name || '');
  const [vendor, setVendor] = useState(data.vendor || '');
  const [examType, setExamType] = useState(data.type || '');
  const [totalMarks, setTotalMarks] = useState(data.totalMarks || '');
  const [examCode, setExamCode] = useState(data.examCode || '');
  const [examTime, setExamTime] = useState(data.examTime || '');
  const [active, setActive] = useState(data.active);
  const [noOfQuestions, setNoOfQuestions] = useState(data.noOfQuestions || '');
  const [domain, setDomain] = useState(data.domain || []);
  const [alertMessage, setAlertMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [isPromptDialogOpen, setPromptDialogOpen] = useState(false);
  const [isQuestionShowDialogOpen, setQuestionShowDialogOpen] = useState(false);
  const [editDomainIndex, setEditDomainIndex] = useState(null);
  const userType = localStorage.getItem('userType');
  const apiUrl = process.env.REACT_APP_API_URL;

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  useEffect(() => {
    async function getDomainAndSubdomain() {
      const apiUrlLocal = process.env.REACT_APP_API_URL;

      try {
        const response = await axios.get(`${apiUrlLocal}/api/AssessmentDomain/${testId}`, {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            'Access-Control-Allow-Origin': '*',
            Authorization: getBasicAuthHeader(),
          },
        });
        setDomain(response.data || []);
      } catch (error) {
        console.error('Error fetching assessment data:', error);
      }
    }
    setId(data.id || '');
    setCourseName(data.courseName || '');
    setExamName(data.name || '');
    setVendor(data.vendor || '');
    setExamType(data.type || '');
    setTotalMarks(data.totalMarks || '');
    setExamCode(data.examCode || '');
    setExamTime(data.examTime || '');
    setNoOfQuestions(data.noOfQuestions || '');
    setActive(data.active);
    getDomainAndSubdomain();
  }, [data]);

  const handleChange = (field, value) => {
    switch (field) {
      case 'id':
        setId(value);
        break;
      case 'courseName':
        setCourseName(value);
        break;
      case 'name':
        setExamName(value);
        break;
      case 'vendor':
        setVendor(value);
        break;
      case 'type':
        setExamType(value);
        break;
      case 'totalMarks':
        setTotalMarks(value);
        break;
      case 'examCode':
        setExamCode(value);
        break;
      case 'examTime':
        setExamTime(value);
        break;
      case 'noOfQuestions':
        setNoOfQuestions(value);
        break;
      case 'domain':
        setDomain(value);
        break;
      default:
        break;
    }
  };

  const showGeneratePrompt = (domainItem) => {
    const generated = generatePrompt(examName, examType, vendor, examTime, totalMarks, examCode, domainItem, noOfQuestions);
    setPrompt(generated);
    setPromptDialogOpen(true);
  };

  const token = localStorage.getItem('token');
  const handleFormSubmit = async () => {
    if (!guardAction('action.assessment.create.save')) return;
    if (!examName || !examTime || !examCode || !vendor || !totalMarks || !examType || !courseName) {
      setAlertMessage('Please fill all mandatory fields before submitting.');
      return;
    }
    setLoading(true);

    const bodyques = {
      testId: data.testId,
      name: examName,
      type: examType,
      vendor,
      examCode,
      examTime,
      totalMarks,
    };
    console.log(bodyques);
    try {
      await axios.put(`${apiUrl}/api/update-assessment`, bodyques, {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          Authorization: getBasicAuthHeader(),
        },
      });

      setLoading(false);
      handleClose();
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const handleEditDomain = (index) => {
    setEditDomainIndex(index);
  };

  const handleSaveDomain = (editedDomain) => {
    const updatedDomain = [...domain];
    updatedDomain[editDomainIndex] = editedDomain;
    setDomain(updatedDomain);
    setEditDomainIndex(null);
  };

  // testId is the reliable "editing an existing assessment" signal — the row may
  // not carry an `id` (e.g. when opened from inside a course), but always has testId.
  const isUpdate = Boolean(data.testId);
  const dialogTitle = isUpdate ? 'Edit assessment' : 'Create assessment';
  const submitLabel = isUpdate ? 'Save' : 'Create';

  if (!open) {
    return (
      <>
        <QuestionsShowdialog open={isQuestionShowDialogOpen} handleClose={() => setQuestionShowDialogOpen(false)} testId={testId} />
        <PromptDialog open={isPromptDialogOpen} handleClose={() => setPromptDialogOpen(false)} prompt={prompt} />
      </>
    );
  }

  return (
    <>
      <QuestionsShowdialog open={isQuestionShowDialogOpen} handleClose={() => setQuestionShowDialogOpen(false)} testId={testId} />
      <PromptDialog open={isPromptDialogOpen} handleClose={() => setPromptDialogOpen(false)} prompt={prompt} />
      <NoticeDialog
        open={Boolean(alertMessage)}
        title="Notice"
        message={alertMessage}
        onClose={() => setAlertMessage('')}
      />
      <ModalShell
        title={dialogTitle}
        sub={courseName || undefined}
        onClose={handleClose}
        max="720px"
        foot={(
          <>
            <Btn type="button" $variant="ghost" onClick={handleClose} disabled={loading}>
              Cancel
            </Btn>
            <Btn type="button" $variant="subtle" onClick={handleFormSubmit} disabled={loading}>
              {loading ? <ButtonSpinnerSlot size={16} /> : submitLabel}
            </Btn>
          </>
        )}
      >
        <FormRoot>
          <GridField>
            <label htmlFor="name">Exam name</label>
            <Input
              id="name"
              value={examName}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          </GridField>

          <TwoColGrid>
            <GridField>
              <label htmlFor="examTime">Exam time (minutes)</label>
              <Input
                id="examTime"
                value={examTime}
                onChange={(e) => handleChange('examTime', e.target.value)}
              />
            </GridField>
            <GridField>
              <label htmlFor="examCode">Exam code</label>
              <Input
                id="examCode"
                value={examCode}
                onChange={(e) => handleChange('examCode', e.target.value)}
              />
            </GridField>
            <GridField>
              <label htmlFor="vendor">Vendor</label>
              <Input
                id="vendor"
                value={vendor}
                onChange={(e) => handleChange('vendor', e.target.value)}
              />
            </GridField>
            <GridField>
              <label htmlFor="totalMarks">Total marks</label>
              <Input
                id="totalMarks"
                value={totalMarks}
                onChange={(e) => handleChange('totalMarks', e.target.value)}
              />
            </GridField>
            <GridField>
              <label htmlFor="type">Exam type</label>
              <Select
                id="type"
                value={examType || ''}
                onChange={(e) => handleChange('type', e.target.value)}
              >
                <option value="">Select type</option>
                <option value="Text">Text</option>
              </Select>
            </GridField>
            <GridField>
              <label htmlFor="courseName">Course name</label>
              <Input id="courseName" value={courseName} disabled readOnly />
            </GridField>
          </TwoColGrid>

          <DomainSection>
            <DomainSectionTitle>Domains</DomainSectionTitle>
            {domain && domain.length > 0 ? (
              domain.map((item, index) => (
                <DomainCard key={index}>
                  <DomainHead>
                    <span>{item.name}</span>
                    {item.percentage != null && item.percentage !== '' && (
                      <DomainPct>{item.percentage}%</DomainPct>
                    )}
                  </DomainHead>
                  {(item.subDomain || []).length > 0 && (
                    <SubList>
                      {item.subDomain.map((subItem, subIndex) => (
                        <li key={subIndex}>
                          {subItem.name}
                          {subItem.sub_topics ? ` — ${subItem.sub_topics}` : ''}
                        </li>
                      ))}
                    </SubList>
                  )}
                  {userType === '2' && (
                    <PromptBtnRow>
                      <Btn type="button" $variant="ghost" $sm onClick={() => showGeneratePrompt(item)}>
                        Show prompt for domain {index + 1}
                      </Btn>
                    </PromptBtnRow>
                  )}
                </DomainCard>
              ))
            ) : (
              <Muted as="p" style={{ margin: 0, fontSize: 13 }}>No domain data available</Muted>
            )}
          </DomainSection>
        </FormRoot>
      </ModalShell>
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
}
